import {
  Box,
  Avatar,
  HStack,
  Menu,
  Text,
  VStack,
  Portal,
  useCheckboxGroup,
  IconButton
} from '@chakra-ui/react';
import { useColorMode, ColorModeIcon } from '../common/color-mode';
import { NavLink } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import { LogoMarkLink } from '../icons/Logo';
import { RiLogoutBoxRLine } from "react-icons/ri";
import { MdAccountBox, MdKeyboard, MdOutlineFeedback, MdOutlineSettings } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { HiLanguage } from 'react-icons/hi2';
import { TbHelp } from 'react-icons/tb';
import { useAuth } from '@/hooks/useAuth';
import { LanguageSelector } from '../common/LanguageSelector';
import { HandleCreateTeam } from '@/components/features/general/team';
import { routeTo } from '@/router/routeTo';
import type { Team } from '@/types/team';
import { useTeams } from '@/hooks/useTeams';


export const Header = () => {
  const { t } = useTranslation(['common']);
  const { user } = useAuth();
  const { selectedTeam, teams, switchTeam } = useTeams();
  const { colorMode, toggleColorMode } = useColorMode()
  const group = useCheckboxGroup({ defaultValue: ["autosave"] })

  const notifications: any[] = []; // Empty array - notifications should come from API
  return (
    <HStack
      mt={6}
      px={4}
      gap={6}
      w="100%"
      justify="space-between"
      align="center"
      flexWrap="wrap"
    >
      {/* Logo */}
      <LogoMarkLink />

      {/* Right section: Notifications + Profile */}
      <HStack gap={4}>

        {/* Team */}
        {teams && teams.length > 0 ? (
          <Menu.Root>
            <Menu.Trigger asChild>
              <IconButton variant="surface" size="md" padding="3">
                {selectedTeam?.name ?? 'Select team'}
              </IconButton>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content minW="10rem">
                  <Menu.RadioItemGroup
                    value={selectedTeam?.id ?? ''}
                    onValueChange={(id) => switchTeam(id.value)}
                  >
                    {teams.map((team: Team) => (
                      <Menu.RadioItem key={team.id} value={team.id}>
                        {team.name}
                        <Menu.ItemIndicator />
                      </Menu.RadioItem>
                    ))}
                  </Menu.RadioItemGroup>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        ) : (
          <HandleCreateTeam />
        )}
        {/* Notifications */}
        <Menu.Root>
          <Menu.Trigger asChild>
            <Box position="relative">
              <IconButton
                aria-label={t("nav.notifications")}
                variant="ghost"
                size="sm"
                borderRadius="full"
              >
                <FaBell />
              </IconButton>
              {/* Badge */}
              <Box
                position="absolute"
                top="-1"
                right="-1"
                bg="red.500"
                color="white"
                fontSize="xs"
                fontWeight="bold"
                borderRadius="full"
                px={1}
                minW="5"
                h="5"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={1}
              >
                {notifications.length}
              </Box>
            </Box>
          </Menu.Trigger>

          {/* Notifications Dropdown */}
          <Portal>
            <Menu.Positioner>
              <Menu.Content minW="sm" p={2}>
                <Menu.ItemGroup>
                  <Menu.ItemGroupLabel>{t("nav.notifications")}</Menu.ItemGroupLabel>
                  {notifications.map((notification) => (
                    <Menu.CheckboxItem
                      key={notification.value}
                      value={notification.value}
                      checked={group.isChecked(notification.value)}
                      onCheckedChange={() => group.toggleValue(notification.value)}
                      disabled={notification.disabled}
                      px={2}
                      py={1}
                    >
                      <VStack align="start" gap={0}>
                        <HStack>
                          {notification.icon && <Box>{notification.icon}</Box>}
                          <Text fontWeight="medium">{t(`notifications.items.${notification.value}.title`)}</Text>
                          <Menu.ItemIndicator />
                        </HStack>
                        {notification.description && (
                          <Text fontSize="sm" color="gray.500">
                            {t(`notifications.items.${notification.value}.description`)}
                          </Text>
                        )}
                      </VStack>
                    </Menu.CheckboxItem>
                  ))}
                </Menu.ItemGroup>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>

        {/* Profile Menu */}
        <Menu.Root closeOnSelect={false}>
          <Menu.Trigger asChild>
            <Box position="relative">
              <Avatar.Root cursor="pointer">
                <Avatar.Image
                  src={user?.picture || undefined}
                  alt={user?.name || "No Name"}
                />
                <Avatar.Fallback>U</Avatar.Fallback>
              </Avatar.Root>
            </Box>
          </Menu.Trigger>

          <Portal>
            <Menu.Positioner >
              <Menu.Content>
                <Menu.ItemGroup>
                  <Menu.Item onClick={toggleColorMode} value="appearance">
                    <Box
                      display="flex"
                      alignItems="center"
                      cursor="pointer"
                    >
                      <ColorModeIcon />
                      <Text ml={2}>
                        {t("theme.label", { mode: t(`theme.${colorMode}.default`) })}
                      </Text>
                    </Box>
                  </Menu.Item>

                  <Menu.Item value="language" asChild>
                    <Box
                      display="flex"
                      alignItems="center"
                      cursor="pointer"
                    >
                      <HiLanguage /><LanguageSelector />
                    </Box>
                  </Menu.Item>
                </Menu.ItemGroup>

                <Menu.Separator />

                <Menu.ItemGroup>
                  <Menu.Item value="account" asChild>
                    <NavLink to={routeTo.accountRoot()}>
                      <HStack>
                        <MdAccountBox />
                        <Text>{t("nav.account")}</Text>
                      </HStack>
                    </NavLink>
                  </Menu.Item>

                  <Menu.Item value="sign-out" asChild>
                    <NavLink to={routeTo.signOut()}>
                      <HStack color="red.500">
                        <RiLogoutBoxRLine />
                        <Text>{t("nav.sign_out")}</Text>
                      </HStack>
                    </NavLink>
                  </Menu.Item>
                </Menu.ItemGroup>

                <Menu.Separator />

                <Menu.ItemGroup>
                  <Menu.Item value="settings" asChild>
                    <NavLink to={routeTo.accountSettings()}>
                      <HStack>
                        <MdOutlineSettings />
                        <Text>{t("nav.settings")}</Text>
                      </HStack>
                    </NavLink>
                  </Menu.Item>

                  { /* Maybe make a pop up window for that */}
                  <Menu.Item value="shortcuts" asChild>
                    <NavLink to={routeTo.dashboard()}>
                      <HStack>
                        <MdKeyboard />
                        <Text>{t("nav.shortcuts")}</Text>
                      </HStack>
                    </NavLink>
                  </Menu.Item>
                </Menu.ItemGroup>

                <Menu.Separator />

                <Menu.ItemGroup>
                  <Menu.Item value="help" asChild>
                    <NavLink to={routeTo.help()}>
                      <HStack>
                        <TbHelp />
                        <Text>{t("nav.help")}</Text>
                      </HStack>
                    </NavLink>
                  </Menu.Item>

                  { /* Maybe make a pop up window for that */}
                  <Menu.Item value="feedback" asChild>
                    <NavLink to={routeTo.dashboard()}>
                      <HStack>
                        <MdOutlineFeedback />
                        <Text>{t("nav.feedback")}</Text>
                      </HStack>
                    </NavLink>
                  </Menu.Item>
                </Menu.ItemGroup>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </HStack>
    </HStack>

  );
};